import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  TextField,
  MenuItem,
  Pagination,
  Grid,
  Button,
  Stack,
  Card,
  CardContent,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import { Report } from "@mui/icons-material";
import DesktopView from "../../Components/views/DesktopView";
import MobileView from "../../Components/views/MobileView";
import dayjs from "dayjs";

const MeetingTable = ({
  data = [],
  loading = false,
  onEdit,
  onSendEmail,
  onReport,
}) => {
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  // Filters
  const [searchTitle, setSearchTitle] = useState("");
  const [searchBatch, setSearchBatch] = useState("");
  const [filterDate, setFilterDate] = useState("");

  // Unique batch names for dropdown
  const uniqueBatches = [...new Set(data.map((i) => i.batch_name))].filter(
    (i) => i
  );

  // Filtering logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const titleMatch = row.title
        .toLowerCase()
        .includes(searchTitle.toLowerCase());

      const batchMatch = searchBatch ? row.batch_name === searchBatch : true;

      const dateMatch = filterDate
        ? row.date.slice(0, 10) === filterDate
        : true;

      return titleMatch && batchMatch && dateMatch;
    });
  }, [data, searchTitle, searchBatch, filterDate]);

  // Pagination slice
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <>
      {/* Top Bar */}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Grid item>
          <Typography variant="subtitle1" fontWeight={700}>
            Total Meetings: {filteredData.length}
          </Typography>
        </Grid>

        <Grid item>
          <Grid container spacing={2}>
            {/* Search Title */}
            <Grid item>
              <TextField
                size="small"
                label="Search Title"
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
              />
            </Grid>

            {/* Batch Filter */}
            <Grid item>
              <TextField
                size="small"
                select
                label="Batch"
                value={searchBatch}
                onChange={(e) => setSearchBatch(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">All</MenuItem>
                {uniqueBatches.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Date Filter */}
            <Grid item>
              <TextField
                size="small"
                label="Filter Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
              />
            </Grid>
            <Grid item>
              {/* Your Reset Button */}
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  setSearchTitle("");
                  setSearchBatch("");
                  setFilterDate("");
                  setPage(1);
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Table */}
      <DesktopView>
        <TableContainer component={Paper} sx={{ borderRadius: "12px" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography textAlign="center" variant="body1">
                No meeting records found.
              </Typography>
            </Box>
          ) : (
            <>
              <Table>
                <TableHead sx={{ background: "#f5f6fa" }}>
                  <TableRow>
                    <TableCell>
                      <b>S.No</b>
                    </TableCell>
                    <TableCell>
                      <b>Title</b>
                    </TableCell>
                    <TableCell>
                      <b>Batch Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Email Body</b>
                    </TableCell>
                    <TableCell>
                      <b>Date</b>
                    </TableCell>
                    <TableCell>
                      <b>Meet Link</b>
                    </TableCell>
                    <TableCell align="center">
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>

                      <TableCell>{row.title}</TableCell>

                      <TableCell>{row.batch_name || "—"}</TableCell>
                      <TableCell>{row.email_body || "—"}</TableCell>

                      <TableCell>
                       {dayjs(row.date).format("DD-MMM-YYYY, hh:mm A")}
                      </TableCell>

                      <TableCell>
                        <a
                          href={row.google_meet_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#1976d2" }}
                        >
                          Open Link
                        </a>
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            color="primary"
                            onClick={() => onEdit(row)}
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            color="secondary"
                            onClick={() => onSendEmail(row)}
                          >
                            <EmailIcon />
                          </IconButton>

                          <IconButton
                            color="secondary"
                            onClick={() => onReport(row)}
                          >
                            <Report />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </TableContainer>
      </DesktopView>
      <MobileView>
        <Box sx={{ p: 1 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Typography textAlign="center" variant="body1" sx={{ mt: 2 }}>
              No meeting records found.
            </Typography>
          ) : (
            paginatedData.map((row, index) => (
              <Card
                key={row.id}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight="bold">
                    {row.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <b>Batch:</b> {row.batch_name || "—"}
                  </Typography>
                  
                  <Typography variant="body2">
                    <b>Date:</b>{" "}
                    {dayjs(row.date).format("DD-MMM-YYYY, hh:mm A")}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <b>Email Body:</b> {row.email_body}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <a
                      href={row.google_meet_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#1976d2", fontWeight: "bold" }}
                    >
                      Join Meet
                    </a>
                  </Typography>
                  {/* Actions */}
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 2 }}
                  >
                    <IconButton color="primary" onClick={() => onEdit(row)}>
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      color="secondary"
                      onClick={() => onSendEmail(row)}
                    >
                      <EmailIcon />
                    </IconButton>

                    <IconButton color="secondary" onClick={() => onReport(row)}>
                      <Report />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </MobileView>

      {/* Pagination */}
      {filteredData.length > rowsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </>
  );
};

export default MeetingTable;
